import {atoms} from '@dropbox/dig-foundations';
import cx from 'classnames';
import {Link} from 'components/DSYS/Link';
import {AnchorHTMLAttributes, forwardRef, MouseEvent, ReactNode} from 'react';
import {
  dashboardLinkDestination,
  logDashboardInteraction,
} from 'views/home/dashboard/dashboardAnalytics';
import {useDashboardTileId} from 'views/home/dashboard/DashboardTileContext';

const tileRowLinkClass = atoms({
  display: 'block',
  textDecoration: 'none',
});

/**
 * Wraps a tile list row in a real anchor so cmd/ctrl/middle-click opens the
 * destination in a new tab (and right-click "Copy link" works), while normal
 * clicks still navigate within the SPA. The row's own padding, hover shade, and
 * layout stay on the inner content; this just provides the link affordance.
 *
 * Pass `to` for an in-app route (react-router) or `href` for an external URL.
 */
type TileRowLinkProps = {
  children: ReactNode;
  /** Optional analytics target label (defaults to `row`). */
  analyticsTarget?: string;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> &
  ({to: string; href?: never} | {href: string; to?: never});

export const TileRowLink = forwardRef<HTMLAnchorElement, TileRowLinkProps>(
  (
    {to, href, children, className, rel, target, analyticsTarget = 'row', onClick, ...anchorProps},
    ref
  ) => {
    const tileId = useDashboardTileId();

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (tileId) {
        const path = to ?? href ?? '';
        logDashboardInteraction({
          action: 'row_click',
          tileId,
          target: analyticsTarget,
          destination: dashboardLinkDestination(path),
        });
      }
      onClick?.(event);
    };

    if (href !== undefined) {
      return (
        <a
          {...anchorProps}
          href={href}
          target={target ?? '_blank'}
          rel={rel ?? 'noopener noreferrer'}
          className={cx(tileRowLinkClass, className)}
          style={{color: 'inherit'}}
          ref={ref}
          onClick={handleClick}
        >
          {children}
        </a>
      );
    }

    return (
      <Link
        {...anchorProps}
        to={to}
        hasNoUnderline
        monochromatic
        className={cx(tileRowLinkClass, className)}
        style={{color: 'inherit'}}
        ref={ref}
        onClick={handleClick}
      >
        {children}
      </Link>
    );
  }
);

TileRowLink.displayName = 'TileRowLink';
